import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { CollectionChannel, CollectionOutcome } from '@aeris/contracts'
import {
  COLLECTION_CONTACT_CHANNELS,
  COLLECTION_CONTACT_OUTCOMES,
} from '@aeris/status-meta'
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  MoneyInput,
  RadioGroup,
  Textarea,
} from '@aeris/ui'

import { recordCollectionContact } from '../lib/api/collections.ts'
import { ApiError } from '../lib/api.ts'

/**
 * "Liguei, prometeu pagar dia 20" (F135).
 *
 * O canal e o resultado são escolhas, não texto livre: é deles que a fila tira
 * "último contato há 2 dias · não atendeu" e a aba "Promessa quebrada". A
 * observação é para o que só uma frase explica.
 *
 * A promessa abre com o vencido inteiro para amanhã — o combinado mais comum —
 * e a pessoa ajusta.
 */

const CHANNELS = COLLECTION_CONTACT_CHANNELS
const OUTCOMES = COLLECTION_CONTACT_OUTCOMES

const localDay = (at: Date): string => {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}

export const CollectionContactDialog = ({
  partyId,
  customerName,
  suggestedAmount,
  onClose,
}: {
  partyId: string
  customerName: string
  /** O vencido do cliente, no formato da API: `"150.00"`. */
  suggestedAmount: string
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [channel, setChannel] = useState<CollectionChannel>('phone')
  const [outcome, setOutcome] = useState<CollectionOutcome>('promised')
  const [note, setNote] = useState('')
  const [amount, setAmount] = useState(suggestedAmount)
  const [day, setDay] = useState(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return localDay(tomorrow)
  })
  const [error, setError] = useState<string | null>(null)

  const promising = outcome === 'promised'
  const incomplete =
    promising && (amount.length === 0 || !/[1-9]/.test(amount) || day === '')

  const save = useMutation({
    mutationFn: async () =>
      await recordCollectionContact(partyId, {
        channel,
        outcome,
        note: note.trim().length === 0 ? null : note.trim(),
        ...(promising ? { promisedAmount: amount, promisedFor: day } : {}),
      }),
    onSuccess: () => {
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['collections'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível registrar o contato.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Registrar contato"
        description={`O que aconteceu na cobrança de ${customerName}.`}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            save.mutate()
          }}
        >
          <Field label="Como">
            {(field) => (
              <RadioGroup
                id={field.id}
                name="channel"
                orientation="horizontal"
                value={channel}
                onValueChange={setChannel}
                options={CHANNELS}
              />
            )}
          </Field>

          <Field label="Resultado">
            {(field) => (
              <RadioGroup
                id={field.id}
                name="outcome"
                value={outcome}
                onValueChange={setOutcome}
                options={OUTCOMES}
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
              />
            )}
          </Field>

          {promising ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Valor prometido">
                {(field) => (
                  <MoneyInput
                    {...field}
                    value={amount}
                    onValueChange={setAmount}
                  />
                )}
              </Field>
              <Field label="Para o dia">
                {(field) => (
                  <DateField
                    {...field}
                    value={day}
                    onChange={(event) => {
                      setDay(event.target.value)
                    }}
                  />
                )}
              </Field>
            </div>
          ) : null}

          <Field
            label="Observação"
            hint={
              promising
                ? 'Se pagar até o dia, a promessa some sozinha. Se não, o cliente aparece em "Promessa quebrada".'
                : undefined
            }
          >
            {(field) => (
              <Textarea
                {...field}
                value={note}
                maxLength={2000}
                onChange={(event) => {
                  setNote(event.target.value)
                }}
                placeholder="Ex.: paga quando cair o salário"
              />
            )}
          </Field>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={incomplete}
              loading={save.isPending}
            >
              Registrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
